import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MapPin, Send, PhoneOff } from 'lucide-react';
import { format } from 'date-fns';
import { formatTimeRange } from '../../utils/time';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'IHCS', employment: 'Employment' };
const SERVICE_COLORS = {
  day_program: { bg: 'bg-[#EDE7F6]', border: 'border-[#9B7ED8]' },
  in_home: { bg: 'bg-[#E8F5E9]', border: 'border-[#81C784]' },
  employment: { bg: 'bg-[#FFF3E0]', border: 'border-[#FFB74D]' },
};

export function ShiftRequestDialog({
  open, onOpenChange, selectedShift,
  requestType, setRequestType, reason, setReason,
  targetEmployeeId, setTargetEmployeeId, coworkers,
  submitting, onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="shift-request-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Request Shift Change
          </DialogTitle>
        </DialogHeader>
        {selectedShift && (
          <div className="space-y-4 py-2">
            <div className={`p-3 rounded-lg border ${SERVICE_COLORS[selectedShift.service_line]?.border} ${SERVICE_COLORS[selectedShift.service_line]?.bg}/50`}>
              <p className="font-semibold text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {format(new Date(selectedShift.date + 'T12:00:00'), 'EEE, MMM d')} | {formatTimeRange(selectedShift.time_in, selectedShift.time_out)}
              </p>
              <p className="text-sm mt-1">{SERVICE_LABELS[selectedShift.service_line]}</p>
              {selectedShift.location && <p className="text-xs text-[#8E85A0] mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedShift.location}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Request Type</Label>
              <Select value={requestType} onValueChange={setRequestType}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cover">Need Coverage</SelectItem>
                  <SelectItem value="swap">Swap with Another</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {requestType === 'swap' && (
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium">Swap with <span className="text-xs text-[#8E85A0] font-normal">(optional — leave blank to open to all)</span></Label>
                <select value={targetEmployeeId} onChange={e => setTargetEmployeeId(e.target.value)}
                  className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white" data-testid="swap-target-select">
                  <option value="">— Any qualified coworker —</option>
                  {coworkers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Reason</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why do you need this change?"
                className="border-[#DED8EB] min-h-[80px]" data-testid="request-reason" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DED8EB]" data-testid="request-cancel-btn">Cancel</Button>
          <Button onClick={onSubmit} disabled={submitting} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="request-submit-btn">
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApplyDialog({ open, onOpenChange, applyTarget, applyMsg, setApplyMsg, applying, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {applyTarget?.title || 'Express Interest'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex items-center gap-2 flex-wrap text-xs text-[#5A4D6E]">
            {applyTarget?.service_line && <Badge className="bg-[#F0EDF6] text-[#5A4D6E]">{SERVICE_LABELS[applyTarget.service_line]}</Badge>}
            {applyTarget?.salary_range && <span>{applyTarget.salary_range}</span>}
            {applyTarget?.deadline && <span className="text-[#8E85A0]">Deadline: {applyTarget.deadline}</span>}
          </div>
          {applyTarget?.description && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Description</p>
              <p className="text-xs text-[#5A4D6E] whitespace-pre-line">{applyTarget.description}</p>
            </div>
          )}
          {applyTarget?.requirements && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Requirements</p>
              <p className="text-xs text-[#5A4D6E] whitespace-pre-line">{applyTarget.requirements}</p>
            </div>
          )}
          {applyTarget?.job_description && (
            <div className="bg-[#F7F4EF] border border-[#DED8EB] rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-[#2D1B4E]">Full Job Description</p>
              {applyTarget.job_description.duties && (
                <div><p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Duties</p><p className="text-xs text-[#5A4D6E] whitespace-pre-line">{applyTarget.job_description.duties}</p></div>
              )}
              {applyTarget.job_description.qualifications && (
                <div><p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Qualifications</p><p className="text-xs text-[#5A4D6E] whitespace-pre-line">{applyTarget.job_description.qualifications}</p></div>
              )}
              {applyTarget.job_description.benefits && (
                <div><p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Benefits</p><p className="text-xs text-[#5A4D6E] whitespace-pre-line">{applyTarget.job_description.benefits}</p></div>
              )}
            </div>
          )}
          <div className="space-y-2 pt-2 border-t border-[#DED8EB]">
            <Label className="text-[#2D1B4E] font-medium">Your Message (optional)</Label>
            <Input value={applyMsg} onChange={e => setApplyMsg(e.target.value)}
              placeholder="Why are you interested?" className="border-[#DED8EB]" data-testid="apply-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DED8EB]">Cancel</Button>
          <Button onClick={onSubmit} disabled={applying} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="apply-submit-btn">
            {applying ? 'Submitting...' : <><Send className="h-3.5 w-3.5" /> Submit</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TimeOffDialog({ open, onOpenChange, toForm, setToForm, toSaving, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm bg-white" data-testid="time-off-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Request Time Off
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#2D1B4E] font-medium text-sm">Start</Label>
              <Input type="date" value={toForm.start_date} onChange={e => setToForm(p => ({ ...p, start_date: e.target.value }))} className="border-[#DED8EB]" data-testid="time-off-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#2D1B4E] font-medium text-sm">End</Label>
              <Input type="date" value={toForm.end_date} onChange={e => setToForm(p => ({ ...p, end_date: e.target.value }))} className="border-[#DED8EB]" data-testid="time-off-end" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#2D1B4E] font-medium text-sm">Type</Label>
            <Select value={toForm.type} onValueChange={v => setToForm(p => ({ ...p, type: v }))}>
              <SelectTrigger className="border-[#DED8EB]" data-testid="time-off-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pto">PTO / Vacation</SelectItem>
                <SelectItem value="sick">Sick Leave</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#2D1B4E] font-medium text-sm">Reason (optional)</Label>
            <Textarea value={toForm.reason} onChange={e => setToForm(p => ({ ...p, reason: e.target.value }))} placeholder="Brief reason..." className="border-[#DED8EB] min-h-[70px]" data-testid="time-off-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DED8EB]">Cancel</Button>
          <Button onClick={onSubmit} disabled={toSaving || !toForm.start_date || !toForm.end_date} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="time-off-submit-btn">
            {toSaving ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CallOutDialog({ shift, onOpenChange, callOutReason, setCallOutReason, callOutSubmitting, onSubmit }) {
  return (
    <Dialog open={!!shift} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="sm:max-w-sm bg-white" data-testid="callout-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <PhoneOff className="h-5 w-5 text-[#D9534F]" /> Call Out of Today's Shift
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {shift && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm font-semibold text-[#2D1B4E]">{shift.date} · {formatTimeRange(shift.time_in || '', shift.time_out || '')}</p>
              <p className="text-xs text-[#5A4D6E]">{SERVICE_LABELS[shift.service_line] || shift.service_line}{shift.location ? ' · ' + shift.location : ''}</p>
            </div>
          )}
          <p className="text-xs text-[#5A4D6E]">Your supervisor will be notified immediately and the shift will reopen for another staff member to claim.</p>
          <div>
            <Label className="text-[#2D1B4E] font-medium">Reason (optional)</Label>
            <Input value={callOutReason} onChange={e => setCallOutReason(e.target.value)} placeholder="Sick, family emergency, etc." className="border-[#DED8EB]" data-testid="callout-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(null)} className="border-[#DED8EB]">Cancel</Button>
          <Button onClick={onSubmit} disabled={callOutSubmitting} className="bg-[#D9534F] hover:bg-[#C62828] text-white gap-1" data-testid="callout-submit-btn">
            <PhoneOff className="h-3.5 w-3.5" /> {callOutSubmitting ? 'Sending...' : 'Confirm Call-Out'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
